import { withEventContext } from "@codat/analytics";
import {
  IndicatorPill,
  SearchIcon,
  TextInput,
  Typography,
  useTheme,
} from "@codat/orchard-ui";
import { useState } from "react";

import ErrorFallback from "components/ErrorFallback";
import Spinner from "components/Spinner";

import styles from "./CategorisationHelp.module.css";
import { useCategories } from "./useCategories";

function CategorisationHelpContent() {
  const [searchValue, setSearchValue] = useState<string | undefined>();
  const { categoriesGrouped, loading, error } = useCategories(searchValue);
  const { colors } = useTheme();

  if (error) {
    return <ErrorFallback />;
  }

  return (
    <div className={styles.pageContainer}>
      <div className={styles.page}>
        <div className={styles.header}>
          <img
            className={styles.logo}
            src="https://static.codat.io/public/branding/codat-blue.png"
            alt="Codat Logo"
            data-testid="categorisation-help-logo"
          />
        </div>
        <Typography variant="h1" as="h1">
          Terminology guide
        </Typography>
        <div className={styles.searchBar}>
          <Typography as="p">
            Descriptions of key accounting terms and core concepts
          </Typography>
          <TextInput
            id="searchTerm"
            placeholder="Search term"
            label="Search term"
            iconLeft={{
              icon: <SearchIcon fillColor={colors["color-ui-neutral-80"]} />,
            }}
            onChange={(event) => setSearchValue(event.target.value)}
            data-testid="categorisation-help-search"
          />
        </div>
        <div className={styles.contentContainer}>
          {loading && (
            <div
              className={styles.spinnerContainer}
              data-testid="categorisation-help-loading"
            >
              <Spinner />
            </div>
          )}
          {!loading && categoriesGrouped && (
            <div
              data-testid="categorisation-help-content"
              className={styles.contentCategories}
            >
              {categoriesGrouped.map((categoryGroup) => (
                <div
                  key={categoryGroup.type}
                  data-testid={"categorisation-help-type-" + categoryGroup.type}
                >
                  <Typography
                    variant="h4"
                    as="h4"
                    className={styles.categoryGroupTitle}
                  >
                    {categoryGroup.type}
                  </Typography>
                  <div className={styles.categoryGroup}>
                    {categoryGroup.categories.map((category) => (
                      <div
                        className={styles.category}
                        key={
                          category.subtypeDisplayName +
                          category.detailTypeDisplayName
                        }
                        data-testid={`categorisation-help-subtype-${category.subtype}-${category.detailType}`}
                      >
                        <IndicatorPill
                          className={styles.pill}
                          label={category.subtypeDisplayName}
                        />
                        <Typography variant="h3" as="h3">
                          {category.detailTypeDisplayName}
                        </Typography>
                        <Typography as="p">
                          {category.detailTypeDescription}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const CategorisationHelp = withEventContext(
  CategorisationHelpContent,
  "Categorisation help page"
);
